import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2,
    InputAdornment,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DatePicker } from "antd";
import { useAuth } from "../../context/AuthProvider";
import { getNoteApprovals, reviewActivityNote } from "../../services/timeTracker.service";
import { encryptData } from "../../utils/cryptoUtils";
import { ROLES, isAdminRole } from "../../utils/roles";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";

const { RangePicker } = DatePicker;

// Mirrors NOTE_REASONS in UserDetailView — the two lists must agree, since a
// note written on the slot grid is reviewed here.
const REASON_LABELS = {
    meeting: "Meeting",
    reading: "Reading",
    planning: "Planning",
    call: "Call",
    break: "Break",
    other: "Other",
};

const STATUS_FILTERS = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "all", label: "All" },
];

const STATUS_CHIP = {
    pending: { label: "Pending", color: "#b45309", bg: "#fef3c7" },
    approved: { label: "Approved", color: "#15803d", bg: "#dcfce7" },
    rejected: { label: "Rejected", color: "#b91c1c", bg: "#fee2e2" },
};

function StatusChip({ status }) {
    const s = STATUS_CHIP[status] || STATUS_CHIP.pending;
    return (
        <Chip
            size="small"
            label={s.label}
            sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, fontSize: 11 }}
        />
    );
}

/**
 * Note Approvals — a manager/admin queue for the notes employees write against
 * untracked 10-minute slots.
 *
 * A slot with no keyboard/mouse counts as 0 worked minutes, so an employee who
 * was in a meeting explains it with a note. The note alone does NOT create
 * worked time; only an approval here credits the slot's missing minutes into
 * the Work Activity day view.
 */
const NoteApprovals = () => {
    const { accessToken } = useAuth();
    const [role, setRole] = useState(null);
    // Defaults to the last month so a reviewer lands on a real backlog rather
    // than on today, which is usually empty.
    const [fromDate, setFromDate] = useState(dayjs().subtract(1, "month"));
    const [toDate, setToDate] = useState(dayjs());
    const [status, setStatus] = useState("pending");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState([]);
    const [busy, setBusy] = useState(false);
    // { ids: [...] } while the reject-reason dialog is open.
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    useEffect(() => {
        if (!accessToken) return;
        try {
            setRole(jwtDecode(accessToken)?.role ?? null);
        } catch (err) {
            console.error("Invalid token", err);
        }
    }, [accessToken]);

    // Managers review alongside admins. Employees never reach the queue — the
    // backend refuses them too, this just avoids showing an empty table.
    const canReview = role != null && (isAdminRole(role) || role === ROLES.MANAGER);

    // A half-cleared picker would send "Invalid Date" to the server, so hold the
    // last request until both ends are real dates.
    const rangeValid = dayjs(fromDate).isValid() && dayjs(toDate).isValid();
    const rangeTooLong = rangeValid && dayjs(toDate).diff(dayjs(fromDate), "day") + 1 > 92;

    const load = useCallback(async () => {
        if (!rangeValid || rangeTooLong) return;
        setLoading(true);
        setError(null);
        setSelected([]);
        try {
            // status goes in the clear; the dates follow the encrypted-payload
            // convention the other time-logger endpoints use.
            const res = await getNoteApprovals(
                encryptData(dayjs(fromDate).format("YYYY-MM-DD")),
                encryptData(dayjs(toDate).format("YYYY-MM-DD")),
                status
            );
            // Response interceptor unwraps axios → res is already the payload.
            setRows(res?.data?.notes || []);
        } catch (err) {
            console.error("Error loading note approvals:", err);
            setError("Failed to load note approvals");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate, status, rangeValid, rangeTooLong]);

    useEffect(() => {
        // Nothing to fetch while the range is unusable — drop the spinner so the
        // table shows the reason instead of spinning forever.
        if (!canReview || !rangeValid || rangeTooLong) {
            setLoading(false);
            return;
        }
        load();
    }, [canReview, load, rangeValid, rangeTooLong]);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            [r.userName, r.email, r.text, REASON_LABELS[r.reason] || r.reason]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q))
        );
    }, [rows, search]);

    // Reset pagination to first page when search, status, or date range changes
    useEffect(() => {
        setPage(0);
    }, [search, status, fromDate, toDate]);

    // Keep page in bounds if filtered rows count shrinks
    useEffect(() => {
        if (page > 0 && page * rowsPerPage >= filteredRows.length) {
            setPage(Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1));
        }
    }, [filteredRows.length, page, rowsPerPage]);

    const paginatedRows = useMemo(() => {
        return filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [filteredRows, page, rowsPerPage]);

    // Only undecided notes can be bulk-actioned; an approved row has nothing to
    // approve again, so it is not selectable.
    const actionableIds = useMemo(
        () => filteredRows.filter((r) => r.status === "pending").map((r) => r._id),
        [filteredRows]
    );

    // Actionable note IDs on the currently viewed page
    const currentPageActionableIds = useMemo(
        () => paginatedRows.filter((r) => r.status === "pending").map((r) => r._id),
        [paginatedRows]
    );

    const isCurrentPageAllSelected =
        currentPageActionableIds.length > 0 &&
        currentPageActionableIds.every((id) => selected.includes(id));

    const isCurrentPageIndeterminate =
        currentPageActionableIds.some((id) => selected.includes(id)) && !isCurrentPageAllSelected;

    const toggleRow = (id) =>
        setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const toggleCurrentPage = () => {
        if (isCurrentPageAllSelected) {
            setSelected((prev) => prev.filter((id) => !currentPageActionableIds.includes(id)));
        } else {
            setSelected((prev) => Array.from(new Set([...prev, ...currentPageActionableIds])));
        }
    };

    const submitReview = async (ids, action, reviewNote = "") => {
        if (!ids.length) return;
        setBusy(true);
        try {
            await reviewActivityNote({ noteIds: ids, action, reviewNote });
            ToastSuccess(
                action === "approve"
                    ? `${ids.length} note${ids.length > 1 ? "s" : ""} approved — time added to work time`
                    : `${ids.length} note${ids.length > 1 ? "s" : ""} rejected`
            );
            await load();
        } catch (err) {
            console.error("Error reviewing note:", err);
            ToastError(err?.message || "Could not update the note");
        } finally {
            setBusy(false);
        }
    };

    const openReject = (ids) => {
        setRejectTarget({ ids });
        setRejectReason("");
    };

    const confirmReject = async () => {
        const ids = rejectTarget?.ids || [];
        setRejectTarget(null);
        await submitReview(ids, "reject", rejectReason.trim());
    };

    // Minutes the visible pending rows would add if all were approved — lets a
    // manager see the size of the decision before making it.
    const pendingMinutes = filteredRows
        .filter((r) => r.status === "pending")
        .reduce((sum, r) => sum + (r.creditableMinutes || 0), 0);

    if (!canReview) {
        return (
            <Box sx={{ p: 5, textAlign: "center" }}>
                <Typography variant="h6" color="error">
                    Access Denied
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Only managers and admins can review activity notes.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: 3 }}>
            {/* Top Toolbar: Search on the left, Filters on the right */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 2,
                }}
            >
                <TextField
                    size="small"
                    placeholder="Search by employee or note…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                        width: { xs: "100%", sm: 300, md: 320 },
                        "& .MuiOutlinedInput-root": {
                            height: 40,
                            borderRadius: "6px",
                            backgroundColor: "#fff",
                            "& fieldset": { borderColor: "#d9d9d9" },
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch("")} title="Clear">
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />

                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    <Select
                        size="small"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        sx={{
                            minWidth: 125,
                            height: 40,
                            borderRadius: "6px",
                            backgroundColor: "#fff",
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#d9d9d9",
                            },
                        }}
                    >
                        {STATUS_FILTERS.map((f) => (
                            <MenuItem key={f.value} value={f.value}>
                                {f.label}
                            </MenuItem>
                        ))}
                    </Select>

                    <RangePicker
                        value={[fromDate, toDate]}
                        format="DD/MM/YYYY"
                        onChange={(dates) => {
                            if (dates && dates[0] && dates[1]) {
                                setFromDate(dates[0]);
                                setToDate(dates[1]);
                            }
                        }}
                        allowClear={false}
                        style={{
                            width: 260,
                            height: 40,
                            borderRadius: 6,
                            borderColor: "#d9d9d9",
                            backgroundColor: "#fff",
                        }}
                    />

                    <Tooltip title="Refresh">
                        <span>
                            <IconButton
                                onClick={load}
                                disabled={loading || busy}
                                sx={{
                                    height: 40,
                                    width: 40,
                                    borderRadius: "6px",
                                    border: "1px solid #d9d9d9",
                                    backgroundColor: "#fff",
                                    color: "#475569",
                                    "&:hover": { backgroundColor: "#f8fafc" },
                                }}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Box>

            {/* Action Bar: Stats on the left, Approve / Reject buttons on the right */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1.5,
                    mb: 1.5,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    {pendingMinutes > 0 && (
                        <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                            {actionableIds.length} pending · <strong>{pendingMinutes} min</strong> to credit
                        </Typography>
                    )}
                    {actionableIds.length > 0 && (
                        <>
                            {selected.length < actionableIds.length ? (
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setSelected(actionableIds)}
                                    sx={{
                                        textTransform: "none",
                                        fontSize: "12px",
                                        color: "#008080",
                                        p: 0,
                                        minWidth: 0,
                                        fontWeight: 600,
                                        "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                                    }}
                                >
                                    Select all {actionableIds.length} pending
                                </Button>
                            ) : (
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setSelected([])}
                                    sx={{
                                        textTransform: "none",
                                        fontSize: "12px",
                                        color: "#64748b",
                                        p: 0,
                                        minWidth: 0,
                                        fontWeight: 600,
                                        "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                                    }}
                                >
                                    Clear selection
                                </Button>
                            )}
                        </>
                    )}
                </Box>

                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Button
                        variant="contained"
                        startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                        disabled={!selected.length || busy}
                        onClick={() => submitReview(selected, "approve")}
                        sx={{
                            height: 36,
                            px: 2,
                            borderRadius: "6px",
                            bgcolor: "#008080",
                            "&:hover": { bgcolor: "#006666" },
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "13px",
                        }}
                    >
                        Approve{selected.length ? ` (${selected.length})` : ""}
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<HighlightOffIcon fontSize="small" />}
                        disabled={!selected.length || busy}
                        onClick={() => openReject(selected)}
                        sx={{
                            height: 36,
                            px: 2,
                            borderRadius: "6px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "13px",
                        }}
                    >
                        Reject{selected.length ? ` (${selected.length})` : ""}
                    </Button>
                </Stack>
            </Box>

            {error && (
                <Typography color="error" sx={{ mb: 1 }}>
                    {error}
                </Typography>
            )}

            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e1e5eb" }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: "#f8fafc" }}>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    size="small"
                                    checked={isCurrentPageAllSelected}
                                    indeterminate={isCurrentPageIndeterminate}
                                    disabled={!currentPageActionableIds.length || busy}
                                    onChange={toggleCurrentPage}
                                />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Time Slot</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">
                                Tracked
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="right">
                                Time to Add
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="center">
                                Action
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                                    <CircularProgress size={28} thickness={4} sx={{ color: "#008080" }} />
                                </TableCell>
                            </TableRow>
                        ) : filteredRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 5, color: "#94a3b8" }}>
                                    {!rangeValid
                                        ? "Pick a valid date range."
                                        : rangeTooLong
                                          ? "Date range cannot exceed 92 days — narrow it down."
                                          : `No ${status === "all" ? "" : status} notes between ${dayjs(fromDate).format("DD MMM YYYY")} and ${dayjs(toDate).format("DD MMM YYYY")}.`}
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedRows.map((r) => {
                                const isPending = r.status === "pending";
                                return (
                                    <TableRow key={r._id} hover>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                checked={selected.includes(r._id)}
                                                disabled={!isPending || busy}
                                                onChange={() => toggleRow(r._id)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {r.userName}
                                            </Typography>
                                            {r.email && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {r.email}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {r.date ? dayjs(r.date).format("DD MMM YYYY") : "—"}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>{r.timeLabel}</TableCell>
                                        <TableCell>{REASON_LABELS[r.reason] || r.reason}</TableCell>
                                        <TableCell sx={{ maxWidth: 260 }}>
                                            <Tooltip title={r.text || ""} placement="top" arrow>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        color: r.text ? "inherit" : "#94a3b8",
                                                    }}
                                                >
                                                    {r.text || "—"}
                                                </Typography>
                                            </Tooltip>
                                            {r.editCount > 0 && (
                                                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                                                    edited {r.editCount}×
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">{r.trackedMinutes} min</TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                color={r.creditableMinutes > 0 ? "#15803d" : "text.secondary"}
                                            >
                                                {r.status === "rejected" ? "—" : `+${r.creditableMinutes} min`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip status={r.status} />
                                            {r.reviewedByName && (
                                                <Tooltip
                                                    title={`${r.reviewedByName}${r.reviewedAt
                                                            ? ` · ${dayjs(r.reviewedAt).format("DD MMM, hh:mm A")}`
                                                            : ""
                                                        }${r.reviewNote ? ` — ${r.reviewNote}` : ""}`}
                                                    placement="top"
                                                    arrow
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ display: "block", color: "#94a3b8", cursor: "help" }}
                                                    >
                                                        by {r.reviewedByName}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                                            {isPending ? (
                                                <Stack direction="row" spacing={0.5} justifyContent="center">
                                                    <Tooltip title="Approve — adds the time to work time" arrow>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={busy}
                                                                onClick={() => submitReview([r._id], "approve")}
                                                                sx={{ color: "#15803d" }}
                                                            >
                                                                <CheckCircleOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Reject" arrow>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={busy}
                                                                onClick={() => openReject([r._id])}
                                                                sx={{ color: "#b91c1c" }}
                                                            >
                                                                <HighlightOffIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    —
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
                {!loading && filteredRows.length > 0 && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            width: "100%",
                            borderTop: "1px solid #e1e5eb",
                            overflowX: "hidden",
                        }}
                    >
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={filteredRows.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                border: "none",
                                "& .MuiTablePagination-toolbar": {
                                    minHeight: "48px",
                                    paddingLeft: { xs: "8px", md: "16px" },
                                    paddingRight: { xs: "8px", md: "16px" },
                                    flexWrap: "wrap",
                                },
                                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                                    fontSize: "13px",
                                    color: "#64748b",
                                    margin: 0,
                                },
                                "& .MuiTablePagination-select": {
                                    fontSize: "13px",
                                    borderRadius: "6px",
                                    padding: "4px 8px",
                                },
                                "& .MuiTablePagination-actions": {
                                    marginLeft: { xs: "4px", md: "16px" },
                                },
                                "& .MuiIconButton-root": {
                                    color: "#008080",
                                    padding: { xs: "4px", md: "6px" },
                                    "&:hover": {
                                        bgcolor: "rgba(0, 128, 128, 0.08)",
                                    },
                                    "&.Mui-disabled": {
                                        color: "#cbd5e1",
                                    },
                                },
                            }}
                        />
                    </Box>
                )}
            </TableContainer>

            {/* Reject reason — optional, but it is what the employee sees on the slot. */}
            <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} fullWidth maxWidth="xs">
                <DialogTitle>
                    Reject {rejectTarget?.ids?.length > 1 ? `${rejectTarget.ids.length} notes` : "note"}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        No time will be added. The reason is shown to the employee on the slot.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={3}
                        size="small"
                        placeholder="Reason (optional)"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        inputProps={{ maxLength: 500 }}
                        // index.scss pins every .MuiInputBase-root to 40px with
                        // overflow hidden, which crushes a multiline field and
                        // clips its first line. Undo it for this box only.
                        sx={{
                            "& .MuiInputBase-root": {
                                height: "auto !important",
                                minHeight: "84px !important",
                                overflow: "auto !important",
                                alignItems: "flex-start",
                                py: 1,
                            },
                            "& .MuiInputBase-root textarea": { resize: "none" },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRejectTarget(null)} sx={{ textTransform: "none" }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={confirmReject}
                        sx={{ textTransform: "none" }}
                    >
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default NoteApprovals;
